import { apiGet } from '@/shared/storage-locations-api';

/**
 * Nexvia's existing geographic catalog (`nexvia-api-web/src/modules/location/`), already used
 * platform-wide (drivers, addresses) — storage reuses the same department -> locality hierarchy
 * rather than inventing one (spec §2). Both endpoints are public (no auth middleware wraps
 * `/locations` in nexvia-api-web's `src/routes.ts`) and read-only; confirmed against
 * `nexvia-api-web/__tests__/location/location-http.int.test.js`:
 *
 * - `GET /locations/departments` answers a BARE ARRAY of `{id, name}`, 19 entries for the implicit
 *   default country (`UY` — every client either omits `country` or sends it explicitly; nothing
 *   here needs to, since this platform serves one country today).
 * - `GET /locations/localities` answers a BARE OBJECT keyed by department NAME (not the id
 *   `/departments` gives), each value an array of `{id, name, department}`. The join key between
 *   the two endpoints is the department's `name`, never its `id`.
 *
 * Reuses this milestone's own `apiGet` (see storage-locations-api.ts's docblock for why this repo
 * has no single shared transport module yet) — harmless here too: these endpoints need no bearer
 * token, and `apiGet` simply omits the `Authorization` header when none is stored.
 */
export interface Department {
  id: string;
  name: string;
}

export interface Locality {
  id: string;
  name: string;
  department: string;
}

/** Keyed by department NAME — see docblock above. */
export type LocalitiesByDepartment = Record<string, Locality[]>;

export function fetchDepartments(): Promise<Department[]> {
  return apiGet<Department[]>('/locations/departments');
}

export function fetchLocalities(): Promise<LocalitiesByDepartment> {
  return apiGet<LocalitiesByDepartment>('/locations/localities');
}
